// Таблицы под расчётом: авансы с датами, форма, билеты, обучение, штрафы и долги, которые ещё
// будут удерживаться. Это подтаблицы образца заказчика («Авансы/дата», «Форма», «Билеты»,
// «Нарушения/штраф»): итог колонки в таблице выше раскрывается в них.
//
// ## Пустые не показываются
//
// У человека без билета таблицы билетов нет: пустой блок с заголовком «Билеты» и словами «ничего
// нет» только утоплял бы настоящие таблицы. Пустое там, где таблица обязана быть (у человека есть
// ведомости, а данных нет), — это сам документ (`SettlementScreen`), а не эти блоки.
//
// ## Авансы сверяются, и расхождение называется
//
// В файле заказчика в блоках аванс 130 000, а в таблице авансов 130 100: одна сотня выдана и не
// удержана (или удержана не там). Так же сверяются и здесь: выдано, удержано в ведомостях, разница.
// Расхождение не ошибка показа, а повод проверить, не потерялась ли выдача.

import type { ReactNode } from 'react';

import type {
  PayslipDebt,
  PayslipPeriodAdvanceItem,
  PayslipPeriodAdvancesSummary,
  PayslipPeriodDebtLine,
  PayslipPeriodFineLine,
  PayslipPeriodOut,
  PayslipPeriodUniformLine,
} from '@/shared/api/endpoints/payslip';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { formatDateOnly } from '@/shared/time/format';
import { countLabel } from '@/shared/text/plural';
import { formatPeriodTitle } from '@/shared/time/period';
import * as Badge from '@/shared/ui/badge';
import { Block } from '@/shared/ui/block';
import { cn } from '@/utils/cn';

interface ЗаголовокКолонки {
  текст: string;
  вправо?: boolean;
}

/** Простая таблица подтаблицы: заголовки, строки и необязательный итог. Всё уже отформатировано. */
function Таблица({
  подпись,
  заголовки,
  строки,
  итог,
}: {
  подпись: string;
  заголовки: readonly ЗаголовокКолонки[];
  строки: readonly (readonly ReactNode[])[];
  итог?: readonly ReactNode[];
}) {
  return (
    <div className='overflow-x-auto'>
      <table className='w-full min-w-max border-collapse text-paragraph-sm'>
        <caption className='sr-only'>{подпись}</caption>
        <thead>
          <tr className='border-b border-stroke-soft-200 text-paragraph-xs text-text-sub-600'>
            {заголовки.map((заголовок) => (
              <th
                key={заголовок.текст}
                scope='col'
                className={cn(
                  'px-5 py-2 font-normal',
                  заголовок.вправо === true ? 'text-right' : 'text-left',
                )}
              >
                {заголовок.текст}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className='divide-y divide-stroke-soft-200'>
          {строки.map((строка, номер) => (
            <tr key={номер} className='align-top'>
              {строка.map((ячейка, позиция) => (
                <td
                  key={позиция}
                  className={cn(
                    'px-5 py-2.5',
                    заголовки[позиция]?.вправо === true &&
                      'whitespace-nowrap text-right tabular-nums',
                  )}
                >
                  {ячейка}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {итог !== undefined && (
          <tfoot>
            <tr className='border-t border-stroke-soft-200 bg-bg-weak-50'>
              {итог.map((ячейка, позиция) => (
                <td
                  key={позиция}
                  className={cn(
                    'px-5 py-2.5 text-label-sm text-text-strong-950',
                    заголовки[позиция]?.вправо === true &&
                      'whitespace-nowrap text-right tabular-nums',
                  )}
                >
                  {ячейка}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

const приглушённо = (текст: string) => <span className='text-text-sub-600'>{текст}</span>;

// ── Авансы ────────────────────────────────────────────────────────────────────────────────

function ЗаметкаСверки({ сверка }: { сверка: PayslipPeriodAdvancesSummary }) {
  return (
    <div className='flex flex-col gap-1 border-b border-stroke-soft-200 px-5 py-3'>
      <p className='text-paragraph-sm text-text-strong-950'>
        Выдано {formatRubles(сверка.issued)}, удержано в ведомостях {formatRubles(сверка.counted)}
        {сверка.difference === 0
          ? ': сходится.'
          : `, разница ${formatRubles(Math.abs(сверка.difference))}.`}
      </p>
      {сверка.note !== null && сверка.note !== undefined && (
        <p className='text-paragraph-xs text-text-sub-600'>{сверка.note}.</p>
      )}
    </div>
  );
}

function Авансы({
  выдачи,
  сверка,
}: {
  выдачи: readonly PayslipPeriodAdvanceItem[];
  сверка: PayslipPeriodAdvancesSummary | null | undefined;
}) {
  if (выдачи.length === 0) {
    return null;
  }
  const видноКто = выдачи.some((выдача) => Boolean(выдача.issued_by_name));
  const заголовки: ЗаголовокКолонки[] = [
    { текст: 'Дата' },
    { текст: 'Сумма', вправо: true },
    ...(видноКто ? [{ текст: 'Кто выдал' }] : []),
    { текст: 'Пометка' },
  ];
  const всего = выдачи.reduce((сумма, выдача) => сумма + выдача.amount, 0);
  return (
    <Block title='Авансы' note={countLabel(выдачи.length, ['выдача', 'выдачи', 'выдач'])} flush>
      {сверка !== null && сверка !== undefined && <ЗаметкаСверки сверка={сверка} />}
      <Таблица
        подпись='Авансы по датам'
        заголовки={заголовки}
        строки={выдачи.map((выдача) => [
          formatDateOnly(выдача.happened_on),
          formatKopecks(выдача.amount),
          ...(видноКто ? [выдача.issued_by_name ?? ''] : []),
          выдача.after_period ? (
            // Переносится словами: одна длинная строка растягивала всю таблицу и уезжала за край.
            <span className='block max-w-64 whitespace-normal text-paragraph-xs text-text-sub-600'>
              выдан после последнего месяца расчёта: ни в одну его ведомость не попал
            </span>
          ) : (
            ''
          ),
        ])}
        итог={['Всего', formatKopecks(всего), ...(видноКто ? [''] : []), '']}
      />
    </Block>
  );
}

// ── Форма ─────────────────────────────────────────────────────────────────────────────────

function Форма({ строки }: { строки: readonly PayslipPeriodUniformLine[] }) {
  if (строки.length === 0) {
    return null;
  }
  const всего = строки.reduce((сумма, строка) => сумма + строка.amount, 0);
  return (
    <Block title='Форма' note='спецодежда, из которой сложился долг' flush>
      <Таблица
        подпись='Выданная форма'
        заголовки={[
          { текст: 'Дата' },
          { текст: 'Позиция' },
          { текст: 'Количество', вправо: true },
          { текст: 'Цена', вправо: true },
          { текст: 'Сумма', вправо: true },
        ]}
        строки={строки.map((строка) => [
          formatDateOnly(строка.issued_on),
          строка.title,
          String(строка.quantity),
          formatKopecks(строка.price),
          formatKopecks(строка.amount),
        ])}
        итог={['Всего', '', '', '', formatKopecks(всего)]}
      />
    </Block>
  );
}

// ── Билеты и обучение ─────────────────────────────────────────────────────────────────────

function ДолгиПоВидам({
  название,
  пояснение,
  строки,
}: {
  название: string;
  пояснение: string;
  строки: readonly PayslipPeriodDebtLine[];
}) {
  if (строки.length === 0) {
    return null;
  }
  const всего = строки.reduce((сумма, строка) => сумма + строка.amount, 0);
  return (
    <Block title={название} note={пояснение} flush>
      <Таблица
        подпись={название}
        заголовки={[
          { текст: 'Дата' },
          { текст: 'За что' },
          { текст: 'Сумма', вправо: true },
          { текст: 'Осталось удержать', вправо: true },
          { текст: 'Состояние' },
        ]}
        строки={строки.map((строка) => [
          formatDateOnly(строка.happened_on),
          строка.description ?? строка.debt_type_title,
          formatKopecks(строка.amount),
          formatKopecks(строка.outstanding),
          <Badge.Root
            key={строка.debt_id}
            variant='lighter'
            size='medium'
            color={строка.status === 'active' ? 'orange' : 'gray'}
          >
            {строка.status_title}
          </Badge.Root>,
        ])}
        итог={['Всего', '', formatKopecks(всего), '', '']}
      />
    </Block>
  );
}

// ── Штрафы ────────────────────────────────────────────────────────────────────────────────

function Штрафы({ строки }: { строки: readonly PayslipPeriodFineLine[] }) {
  if (строки.length === 0) {
    return null;
  }
  // Основание видно не всем: у того, кому его не показывают, столбца нет вовсе, а не пустой.
  const видноОснование = строки.some((строка) => Boolean(строка.basis));
  const всего = строки.reduce((сумма, строка) => сумма + строка.amount, 0);
  return (
    <Block title='Штрафы' note='удержано в ведомостях' flush>
      <Таблица
        подпись='Штрафы по ведомостям'
        заголовки={[
          { текст: 'Месяц' },
          { текст: 'Объект' },
          { текст: 'Сумма', вправо: true },
          ...(видноОснование ? [{ текст: 'Основание' }] : []),
        ]}
        строки={строки.map((строка) => [
          formatPeriodTitle({ year: строка.period_year, month: строка.period_month }),
          строка.site_name ?? 'Аппарат управления',
          formatKopecks(строка.amount),
          ...(видноОснование ? [строка.basis ?? приглушённо('не указано')] : []),
        ])}
        итог={['Всего', '', formatKopecks(всего), ...(видноОснование ? [''] : [])]}
      />
    </Block>
  );
}

// ── Долги, которые ещё будут удерживаться ─────────────────────────────────────────────────

function ДействующиеДолги({ долги }: { долги: readonly PayslipDebt[] }) {
  if (долги.length === 0) {
    return null;
  }
  return (
    <Block title='Что ещё будет удерживаться' note='действующие долги человека' flush>
      <Таблица
        подпись='Действующие долги'
        заголовки={[
          { текст: 'Долг' },
          { текст: 'Осталось', вправо: true },
          { текст: 'Как удерживается' },
        ]}
        строки={долги.map((долг) => [
          долг.description ?? долг.debt_type_title,
          formatKopecks(долг.outstanding),
          долг.schedule_note ?? '',
        ])}
      />
    </Block>
  );
}

export function Подтаблицы({ данные }: { данные: PayslipPeriodOut }) {
  return (
    <>
      <Авансы выдачи={данные.advances} сверка={данные.advances_summary} />
      <Форма строки={данные.uniform} />
      <ДолгиПоВидам
        название='Билеты'
        пояснение='дорога до объекта, с датой'
        строки={данные.tickets}
      />
      <ДолгиПоВидам
        название='Обучение'
        пояснение='оплачено компанией, удерживается частями'
        строки={данные.training}
      />
      <Штрафы строки={данные.fines} />
      <ДействующиеДолги долги={данные.debts} />
    </>
  );
}
